import { GrantStatus, uiTargetViews } from '@constants';
import { httpRequest } from '@lib';
import { updateStore } from '@app/store';
import { useMutation } from '@tanstack/react-query';
import { communicateResponseToSDK } from '@utils/general';
import { notification } from '@components/notification';

const updateConsentPreference = async (payload) => {
  try {
    const {
      isSdk,
      consentProfilePurposes,
      preferenceUpdateUrl,
    } = payload;

    const buildPreferences = (purpose) => {
      const channelState = purpose.channelState || {};
      return (purpose.communicationPreferences || [])
        // A mandatory + non-withdrawable CP is immutable on the backend; sending
        // it back triggers "its state cannot be changed". Only include channels
        // the user was allowed to modify (non-mandatory or withdrawable).
        .filter((cp) => cp?.channel && (!cp.mandatory || cp.withdrawableAfterCollect))
        .map((cp) => ({
          channel: cp.channel,
          state: channelState[cp.channel] === GrantStatus.GRANTED ? GrantStatus.GRANTED : GrantStatus.REVOKED,
        }));
    };

    const requestPayload = {
      consentProfilePurposes: consentProfilePurposes.map(({
        consentProfileId, purposes, recordId, consentProfileVersion,
      }) => ({
        consentProfileId,
        recordId,
        consentProfileVersion,
        purposes: purposes.map((purpose) => ({
          purpose: purpose.purposeId,
          consent: purpose.selected ? GrantStatus.GRANTED : GrantStatus.REVOKED,
          preferences: buildPreferences(purpose),
        })),
      })),
      uiTargetView: isSdk ? uiTargetViews.WEB_VIEW : uiTargetViews.NON_INTEGRATED_VIEW,
    };

    const { data } = await httpRequest.post(preferenceUpdateUrl, requestPayload, {
      ignoreAuthToken: true,
    });

    return data;
  } catch (err) {
    updateStore({ errors: err.response.data.errors, response: { data: err.response.data, type: 'updateConsentPreference.failed' } });
    throw new Error(err);
  }
};

export const useUpdateConsentPreference = (eup = false) => {
  return useMutation({
    mutationFn: updateConsentPreference,
    onSuccess: (respData) => {
      communicateResponseToSDK(respData);
      updateStore({
        trackingId: respData?.data.trackingId,
      });
      if (eup) {
        notification.success(respData?.message || 'Consent preferences updated successfully');
      }
    },
  });
};
